import { create, all } from 'mathjs';

// Create a restricted math.js instance with only safe functions
const math = create(all, {
  evaluateSafe: true,
});

export class FormulaEvaluator {
  evaluateFormula(formula, formData, form, decimals = 2, alwaysRoundUp = false) {
    try {
      // Replace field references with their values
      const preparedFormula = this.prepareFormula(formula, formData, form);

      // Handle IF THEN ELSE statements first
      const processedFormula = this.handleConditional(preparedFormula);

      // Evaluate the formula
      const result = math.evaluate(processedFormula);

      // Rounding logic
      let roundedResult;
      const decimalsNum = typeof decimals === 'string' ? parseInt(decimals, 10) : (decimals ?? 2);
      const alwaysRound = alwaysRoundUp === true || alwaysRoundUp === 'true';
      if (alwaysRound) {
        const factor = Math.pow(10, decimalsNum);
        roundedResult = Math.ceil(result * factor) / factor;
      } else {
        roundedResult = Number(Number(result).toFixed(decimalsNum));
      }
      return roundedResult;
    } catch (error) {
      console.error('Formula evaluation error:', error, { formula, formData });
      return 0;
    }
  }

  prepareFormula(formula, formData, form) {
    // Normalize Dutch decimal commas to periods so mathjs can parse them.
    // Field references are wrapped in {…} and never contain commas, so this
    // global swap is safe.
    let cleanedFormula = formula.replace(/,/g, '.');
    // Vervang dubbele plustekens (ook met spaties) door één enkele '+'
    cleanedFormula = cleanedFormula.replace(/(\+\s*){2,}/g, '+');
    return cleanedFormula.replace(/\{([^}]+)\}/g, (match, fieldId) => {
      const value = formData[fieldId];

      if (value === undefined) {
        return 0;
      }

      // If no form object is provided (e.g., in configurator context with product options),
      // directly return the numeric value
      if (!form || !form.pages) {
        return this.getFieldValue(value);
      }

      // Find the element in the form structure
      const element = form.pages
        .flatMap((page) => page.elements)
        .find((element) => element.id === fieldId);

      if (!element) {
        // If element not found in form, try to return the value directly
        return this.getFieldValue(value);
      }

      // For single/multiple choice fields, look up the calculation value
      if (
        element.type === 'single-choice' ||
        element.type === 'multiple-choice' ||
        element.type === 'picture-choice'
      ) {
        if (Array.isArray(value)) {
          return value.reduce((sum, choiceId) => {
            const selectedChoice = element.props.choices.find((choice) => choice.id === choiceId);
            return sum + (selectedChoice ? parseFloat(selectedChoice.calculation_value) || 0 : 0);
          }, 0);
        }
        const selectedChoice = element.props.choices.find((choice) => choice.id === value);
        return selectedChoice ? parseFloat(selectedChoice.calculation_value) || 0 : 0;
      }

      return this.getFieldValue(value);
    });
  }

  getFieldValue(value) {
    if (value === undefined || value === null) return 0;

    if (Array.isArray(value)) {
      // For multiple choice fields, return the number of selected options
      return value.length;
    }

    if (typeof value === 'boolean') {
      // For single choice fields, convert boolean to number
      return value ? 1 : 0;
    }

    // For number fields, ensure we return a number
    const numValue = parseFloat(value);
    return isNaN(numValue) ? 0 : numValue;
  }

  handleConditional(formula) {
    // Converteer && naar 'and' en || naar 'or' voor mathjs compatibiliteit
    let processedFormula = formula.replace(/&&/g, ' and ').replace(/\|\|/g, ' or ');

    // Recursief IF THEN ELSE omzetten naar ternary expressies
    // We verwerken van rechts naar links (binnenste/laatste IF eerst)
    const maxIterations = 20;
    let iteration = 0;

    // Check voor IF (met of zonder haakjes erachter)
    while (/\bIF\b/i.test(processedFormula) && iteration < maxIterations) {
      const before = processedFormula;

      // Patroon dat de LAATSTE (meest rechtse) IF THEN ELSE matched
      // Dit zorgt ervoor dat we van binnen naar buiten werken
      processedFormula = this.replaceLastIfThenElse(processedFormula);

      // Als er niets veranderd is, stop
      if (processedFormula === before) {
        console.warn('handleConditional: geen match gevonden, stoppen', processedFormula);
        break;
      }
      iteration++;
    }

    return processedFormula;
  }

  replaceLastIfThenElse(formula) {
    // Vind de laatste IF in de string (als heel woord)
    const lowerFormula = formula.toLowerCase();
    let lastIfIndex = -1;
    let searchIndex = 0;

    while (true) {
      const idx = lowerFormula.indexOf('if', searchIndex);
      if (idx === -1) break;

      // Check of het een heel woord is (niet deel van een ander woord)
      const charBefore = idx > 0 ? lowerFormula[idx - 1] : ' ';
      const charAfter = idx + 2 < lowerFormula.length ? lowerFormula[idx + 2] : ' ';

      if (!/[a-z0-9]/i.test(charBefore) && !/[a-z0-9]/i.test(charAfter)) {
        lastIfIndex = idx;
      }
      searchIndex = idx + 1;
    }

    if (lastIfIndex === -1) return formula;

    // Neem het deel vanaf de laatste IF
    const beforeIf = formula.substring(0, lastIfIndex);
    const fromIf = formula.substring(lastIfIndex);

    // Probeer verschillende patronen

    // Patroon 1: IF (condition) THEN (expression) ELSE (expression)
    // Met haakjes rond conditie
    const PATTERN_WITH_PARENS =
      /^IF\s*\(\s*([^)]+)\s*\)\s*THEN\s+(\([^)]+\))\s+ELSE\s+(\([^)]+\))/i;

    // Patroon 2: IF condition THEN (expression) ELSE (expression)
    // Zonder haakjes rond conditie - conditie eindigt bij THEN
    const PATTERN_NO_PARENS = /^IF\s+(.+?)\s+THEN\s+(\([^)]+\))\s+ELSE\s+(\([^)]+\))/i;

    // Patroon 3: IF (condition) THEN value ELSE value (simpele waardes)
    const PATTERN_SIMPLE_WITH_PARENS =
      /^IF\s*\(\s*([^)]+)\s*\)\s*THEN\s+([\d.]+)\s+ELSE\s+([\d.]+)/i;

    // Patroon 4: IF condition THEN value ELSE value (simpele waardes, geen haakjes)
    const PATTERN_SIMPLE_NO_PARENS = /^IF\s+(.+?)\s+THEN\s+([\d.]+)\s+ELSE\s+([\d.]+)/i;

    // Patroon 5: IF (condition) THEN (expression) ELSE value
    const PATTERN_MIXED_1 = /^IF\s*\(\s*([^)]+)\s*\)\s*THEN\s+(\([^)]+\))\s+ELSE\s+([\d.]+)/i;

    // Patroon 6: IF condition THEN (expression) ELSE value
    const PATTERN_MIXED_2 = /^IF\s+(.+?)\s+THEN\s+(\([^)]+\))\s+ELSE\s+([\d.]+)/i;

    // Patroon 7: IF (condition) THEN value ELSE (expression)
    const PATTERN_MIXED_3 = /^IF\s*\(\s*([^)]+)\s*\)\s*THEN\s+([\d.]+)\s+ELSE\s+(\([^)]+\))/i;

    // Patroon 8: IF condition THEN value ELSE (expression)
    const PATTERN_MIXED_4 = /^IF\s+(.+?)\s+THEN\s+([\d.]+)\s+ELSE\s+(\([^)]+\))/i;

    const patterns = [
      PATTERN_WITH_PARENS,
      PATTERN_NO_PARENS,
      PATTERN_SIMPLE_WITH_PARENS,
      PATTERN_SIMPLE_NO_PARENS,
      PATTERN_MIXED_1,
      PATTERN_MIXED_2,
      PATTERN_MIXED_3,
      PATTERN_MIXED_4,
    ];

    for (const pattern of patterns) {
      const match = fromIf.match(pattern);
      if (match) {
        const [fullMatch, condition, thenExpr, elseExpr] = match;
        const ternary = `((${condition.trim()}) ? (${thenExpr.trim()}) : (${elseExpr.trim()}))`;
        const afterMatch = fromIf.substring(fullMatch.length);
        return beforeIf + ternary + afterMatch;
      }
    }

    // Fallback: geen match gevonden
    console.warn('replaceLastIfThenElse: geen patroon matched voor:', fromIf.substring(0, 100));
    return formula;
  }
}
